// contexts/illustrations/components/BookLibrary/BookLibrary.tsx

'use client';

import { useMemo, useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import { useIllustrationLibrary } from './hooks/useBookLibrary';
import { LibraryFilters } from './components/LibraryFilters';
import { IllustrationTableView } from './components/BookTableView';
import { EmptyLibrary } from './components/EmptyLibrary';
import { FavoritesEmptyState } from './components/FavoritesEmptyState';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ListFilters, useSearchFilter, SortDropdown, LanguageFilter, LibraryPagination } from '@/shared/components/list';
import type { SortOption } from '@/shared/components/list';
import { useCurrentUser } from '@/contexts/authentication/client';
import { useStories } from '@/contexts/stories/hooks/useStories';
import { cn } from '@/shared/utils/cn';
import { Heart, Plus } from 'lucide-react';

interface IllustrationLibraryProps {
  tokenBalance?: number;
}

function IllustrationCardSkeleton() {
  return (
    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg bg-card">
      <Skeleton className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-8 w-32" />
        </div>
        <Skeleton className="h-5 w-24" />
      </div>

      {/* Filters skeleton */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-10 w-36" />
      </div>

      {/* Grid skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <IllustrationCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

export function IllustrationLibrary({ tokenBalance }: IllustrationLibraryProps) {
  const t = useTranslations('illustrations.library');
  const tFav = useTranslations('illustrations.favorites');
  const locale = useLocale();
  const { user } = useCurrentUser();
  const {
    illustrations,
    totalIllustrations,
    loading,
    error,
    filter,
    setFilter,
    sort,
    setSort,
    sortOrder,
    setSortOrder,
    languageFilter,
    setLanguageFilter,
    languageOptions,
    onlyFavorites,
    setOnlyFavorites,
    deleteIllustration,
    toggleFavorite,
  } = useIllustrationLibrary(user?.familyId);

  const { data: storiesData } = useStories(user?.familyId);

  const storyMap = useMemo(() => {
    const map = new Map<string, string>();
    if (storiesData?.stories) {
      for (const story of storiesData.stories) {
        map.set(story.id, story.title);
      }
    }
    return map;
  }, [storiesData]);

  const { searchValue, debouncedValue: searchQuery, setSearchValue } = useSearchFilter();

  const hasFilter = filter !== 'all' || searchQuery.trim() !== '' || onlyFavorites;

  const filteredIllustrations = useMemo(() => {
    if (!searchQuery.trim()) return illustrations;
    const query = searchQuery.toLowerCase();
    return illustrations.filter((illustration) => illustration.title.toLowerCase().includes(query));
  }, [illustrations, searchQuery]);

  // Client-side pagination (books load in full via useBooks).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  useEffect(() => { setPage(1); }, [searchQuery, filter, languageFilter, onlyFavorites, sort, sortOrder]);
  const pagedIllustrations = useMemo(
    () => filteredIllustrations.slice((page - 1) * pageSize, page * pageSize),
    [filteredIllustrations, page, pageSize],
  );

  const sortOptions: SortOption[] = useMemo(() => [
    { value: 'date', label: t('filters.sortOptions.date') },
    { value: 'title', label: t('filters.sortOptions.title') },
    { value: 'status', label: t('filters.sortOptions.status') },
  ], [t]);

  const sortLabels = useMemo(() => ({
    sortBy: t('filters.sort'),
    sortOrder: t('sort.sortOrder'),
    asc: t('sort.asc'),
    desc: t('sort.desc'),
  }), [t]);

  if (loading) {
    return <LibrarySkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12 text-destructive">
        {t('loadError')}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-3 sm:space-y-6" data-testid="illustration-library-container">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground">{t('header.title')}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {totalIllustrations === 0
              ? t('empty.title')
              : searchQuery.trim()
                ? t('found', { count: filteredIllustrations.length })
                : t('found', { count: totalIllustrations })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button asChild data-testid="illustration-library-create-button">
            <Link href={`/${locale}/create?job=hero`}>
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('createBook')}</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      {(totalIllustrations > 0 || onlyFavorites) && (
        <ListFilters
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t('filters.search')}
          searchTestId="illustration-library-search-input"
        >
          <Button
            variant={onlyFavorites ? 'default' : 'outline'}
            size="sm"
            className="h-8 sm:h-9 gap-1.5 text-xs sm:text-sm"
            aria-pressed={onlyFavorites}
            data-testid="illustration-favorites-filter-toggle"
            onClick={() => setOnlyFavorites(!onlyFavorites)}
          >
            <Heart
              className={cn(
                'h-3.5 w-3.5',
                onlyFavorites ? 'fill-current' : '',
              )}
            />
            <span className="hidden sm:inline">{tFav('onlyFavorites')}</span>
          </Button>
          <LibraryFilters
            filter={filter}
            onFilterChange={setFilter}
          />
          <LanguageFilter
            value={languageFilter}
            onChange={setLanguageFilter}
            options={languageOptions}
            testId="illustration-library-language-filter"
          />
          <SortDropdown
            sortBy={sort}
            sortOrder={sortOrder}
            options={sortOptions}
            onSortByChange={(v) => setSort(v as 'date' | 'title' | 'status')}
            onSortOrderChange={setSortOrder}
            labels={sortLabels}
            testId="illustration-library-sort-button"
          />
        </ListFilters>
      )}

      {/* Books Grid or Empty State */}
      {filteredIllustrations.length === 0 ? (
        onlyFavorites ? (
          <FavoritesEmptyState onClearFilter={() => setOnlyFavorites(false)} />
        ) : (
          <EmptyLibrary hasFilter={hasFilter} />
        )
      ) : (
        <div className="space-y-3">
          <IllustrationTableView
            illustrations={pagedIllustrations}
            storyName={(storyId) => storyMap.get(storyId)}
            onDelete={deleteIllustration}
            onToggleFavorite={toggleFavorite}
          />
          <LibraryPagination
            page={page}
            pageSize={pageSize}
            total={filteredIllustrations.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            testIdPrefix="book-library"
          />
        </div>
      )}
    </div>
  );
}
